import axios from 'axios';

import { BANCO_SYNC_HABILITADO, logGeralDoBanco } from './bancoSync/bancoSyncApi';
import { FIPE_SYNC_HABILITADO, logGeralDaFipe } from './fipeSync/fipeSyncApi';
import { logGeralDeRequisicoes, ORCAMENTO_CILIA_HABILITADO } from './orcamentoCiliaApi';
import { logGeralDoPortal, PORTAL_COTACOES_HABILITADO } from './portalCotacoes/portalCotacoesApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Entidades que o `GET /audit-logs` sabe exibir — espelho da whitelist do backend. */
export type AuditLogEntidade =
  | 'user'
  | 'checklist_edit_permission'
  // at-90: as duas matrizes novas — quem comenta/responde e quem vê qual aba da Auditoria.
  | 'comentario_permissao'
  | 'auditoria_aba_permissao'
  // at-114: cadastros da Oficina Externa (regiões, oficinas, etapas, checklist da visita,
  // parâmetros) e as duas ações de estado do inspetor (cadastro do veículo e saída).
  | 'oficina_externa';

export interface AuditLogDetalhe {
  rotulo: string;
  de: string | null;
  para: string | null;
}

export interface AuditLogItem {
  id: string;
  action: string;
  actionLabel: string;
  createdAt: string;
  performedByName: string | null;
  alvo: string | null;
  descricao: string;
  detalhes: AuditLogDetalhe[];
}

export interface AuditLogsResponse {
  total: number;
  page: number;
  limit: number;
  logs: AuditLogItem[];
}

export async function fetchAuditLogs(params: {
  entity: AuditLogEntidade;
  entityId?: string;
  page?: number;
  limit?: number;
}): Promise<AuditLogsResponse> {
  const response = await axios.get<AuditLogsResponse>(`${BACKEND_URL}/audit-logs`, {
    params,
    withCredentials: true,
    headers: authHeader(),
  });

  return response.data;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Log geral do sistema (transversal/at-91, ADR 0062): tudo o que aconteceu, com quem, quando, onde
// e se deu certo. ADMIN e TI.
//
// 🔴 SEM nenhum catch: numa tela de auditoria, "ninguém fez nada" e "não consegui perguntar" levam
// a conclusões opostas. Quem trata o erro é a tela (regra da at-60).
// ─────────────────────────────────────────────────────────────────────────────────────────────

export type TipoDeAtor = 'USUARIO' | 'PUBLICO' | 'INTEGRACAO' | 'SISTEMA';

export const ROTULO_DO_TIPO_DE_ATOR: Record<TipoDeAtor, string> = {
  USUARIO: 'Usuário',
  PUBLICO: 'Público (sem login)',
  INTEGRACAO: 'Integração',
  SISTEMA: 'Sistema',
};

export interface FiltrosDoLogGeral {
  de?: string;
  ate?: string;
  pessoa?: string;
  atorTipo?: TipoDeAtor;
  modulo?: string;
  acao?: string;
  entidade?: string;
  entidadeId?: string;
  soFalhas?: boolean;
  q?: string;
  incluirNegocio?: boolean;
}

export interface LogGeralItem {
  id: string;
  createdAt: string;
  tipo: 'REQUISICAO' | 'NEGOCIO';
  atorTipo: TipoDeAtor;
  quem: string;
  atorPapel: string | null;
  usuarioId: string | null;
  modulo: string | null;
  acao: string;
  resumo: string;
  entidade: string;
  entidadeId: string | null;
  /** "Veículo ABC1D23 · Onix", "Usuário Maria Souza": tipo em português + nome resolvido (ou id curto). */
  registro: string;
  metodo: string | null;
  rota: string | null;
  statusCode: number | null;
  sucesso: boolean;
  requestId: string | null;
}

export interface LogGeralDetalhe extends LogGeralItem {
  ip: string | null;
  userAgent: string | null;
  payload: unknown;
  changes: Record<string, { antes: unknown; depois: unknown }> | null;
  reason: unknown;
  linhasDaMesmaRequisicao: LogGeralItem[];
}

export interface OpcoesDoLogGeral {
  modulos: string[];
  acoes: string[];
  tiposDeAtor: Array<{ valor: TipoDeAtor; rotulo: string }>;
}

/** Os filtros como a rota espera (booleanos viram 'true', vazio sai). */
export function parametrosDoLogGeral(f: FiltrosDoLogGeral): Record<string, string> {
  const p: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    p[k] = v === true ? 'true' : String(v);
  }
  return p;
}

/** Registros por página na tela Logs do sistema — o padrão de listagem do sistema. */
export const ITENS_POR_PAGINA_DO_LOG = 20;

export interface PaginaDoLogGeral {
  itens: LogGeralItem[];
  pagina: number;
  total: number;
  totalPaginas: number;
}

function parametrosDaPagina(filtros: FiltrosDoLogGeral, pagina: number): Record<string, string> {
  return { ...parametrosDoLogGeral(filtros), pagina: String(pagina), limite: String(ITENS_POR_PAGINA_DO_LOG) };
}

export async function listarLogGeral(filtros: FiltrosDoLogGeral, pagina = 1): Promise<PaginaDoLogGeral> {
  const params = parametrosDaPagina(filtros, pagina);
  const response = await axios.get(`${BACKEND_URL}/audit-logs/geral`, { params, withCredentials: true, headers: authHeader() });
  return response.data;
}

export async function obterOpcoesDoLogGeral(): Promise<OpcoesDoLogGeral> {
  const response = await axios.get(`${BACKEND_URL}/audit-logs/geral/opcoes`, { withCredentials: true, headers: authHeader() });
  return response.data;
}

export async function obterLogGeral(id: string): Promise<LogGeralDetalhe> {
  const response = await axios.get(`${BACKEND_URL}/audit-logs/geral/${encodeURIComponent(id)}`, {
    withCredentials: true,
    headers: authHeader(),
  });
  return response.data;
}

/** Baixa o CSV. Devolve se o arquivo foi cortado no limite de 50 mil linhas. */
export async function exportarLogGeral(filtros: FiltrosDoLogGeral): Promise<{ blob: Blob; nome: string; cortado: boolean }> {
  const response = await axios.get(`${BACKEND_URL}/audit-logs/geral/export`, {
    params: parametrosDoLogGeral(filtros),
    responseType: 'blob',
    withCredentials: true,
    headers: authHeader(),
  });
  const disposicao = String(response.headers?.['content-disposition'] ?? '');
  const nome = /filename="([^"]+)"/.exec(disposicao)?.[1] ?? 'logs-do-sistema.csv';
  return { blob: response.data as Blob, nome, cortado: response.headers?.['x-exportacao-cortada'] === 'true' };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Os OUTROS sistemas (transversal/at-91, fase 5): cada serviço satélite tem o seu log, com a mesma
// rota e a mesma forma de resposta. A tela escolhe o sistema; quem não está ligado neste ambiente
// (env ausente) não aparece no seletor.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export type SistemaDoLog = 'SOAC' | 'COTACOES' | 'REQUISICOES' | 'BANCO' | 'FIPE';

interface ClienteDoSistema {
  listar: (params: Record<string, string>) => Promise<PaginaDoLogGeral>;
  opcoes: () => Promise<OpcoesDoLogGeral>;
  detalhe: (id: string) => Promise<LogGeralDetalhe>;
  exportar: (params: Record<string, string>) => Promise<{ data: Blob; headers: Record<string, unknown> }>;
}

export const SISTEMAS_DO_LOG: Array<{ valor: SistemaDoLog; rotulo: string; habilitado: () => boolean; cliente: () => ClienteDoSistema }> = [
  {
    valor: 'SOAC',
    rotulo: 'Oficina (SOAC)',
    habilitado: () => true,
    cliente: () => ({
      listar: (params) => listarLogGeral(params as FiltrosDoLogGeral, Number(params.pagina) || 1),
      opcoes: obterOpcoesDoLogGeral,
      detalhe: obterLogGeral,
      exportar: async (params) => {
        const r = await axios.get(`${BACKEND_URL}/audit-logs/geral/export`, {
          params,
          responseType: 'blob',
          withCredentials: true,
          headers: authHeader(),
        });
        return { data: r.data, headers: r.headers as Record<string, unknown> };
      },
    }),
  },
  { valor: 'COTACOES', rotulo: 'Portal de Cotações', habilitado: () => PORTAL_COTACOES_HABILITADO, cliente: () => logGeralDoPortal as ClienteDoSistema },
  { valor: 'REQUISICOES', rotulo: 'Requisições', habilitado: () => ORCAMENTO_CILIA_HABILITADO, cliente: () => logGeralDeRequisicoes as ClienteDoSistema },
  { valor: 'BANCO', rotulo: 'Base de dados (sincronização)', habilitado: () => BANCO_SYNC_HABILITADO, cliente: () => logGeralDoBanco as ClienteDoSistema },
  { valor: 'FIPE', rotulo: 'Tabela FIPE', habilitado: () => FIPE_SYNC_HABILITADO, cliente: () => logGeralDaFipe as ClienteDoSistema },
];

const clienteDe = (sistema: SistemaDoLog): ClienteDoSistema =>
  (SISTEMAS_DO_LOG.find((s) => s.valor === sistema) ?? SISTEMAS_DO_LOG[0]).cliente();

export function listarLogGeralDe(sistema: SistemaDoLog, filtros: FiltrosDoLogGeral, pagina = 1) {
  if (sistema === 'SOAC') return listarLogGeral(filtros, pagina);
  return clienteDe(sistema).listar(parametrosDaPagina(filtros, pagina));
}

export function obterOpcoesDoLogGeralDe(sistema: SistemaDoLog) {
  return clienteDe(sistema).opcoes();
}

export function obterLogGeralDe(sistema: SistemaDoLog, id: string) {
  return clienteDe(sistema).detalhe(id);
}

export async function exportarLogGeralDe(sistema: SistemaDoLog, filtros: FiltrosDoLogGeral) {
  if (sistema === 'SOAC') return exportarLogGeral(filtros);
  const r = await clienteDe(sistema).exportar(parametrosDoLogGeral(filtros));
  const disposicao = String(r.headers?.['content-disposition'] ?? '');
  const nome = /filename="([^"]+)"/.exec(disposicao)?.[1] ?? `logs-${sistema.toLowerCase()}.csv`;
  return { blob: r.data, nome, cortado: r.headers?.['x-exportacao-cortada'] === 'true' };
}
